/**
 * Showing untrusted file names safely, and checking the names a person types.
 *
 * A plugin proposes the name in a Save dialog, and a Save dialog's whole job is
 * to be accurate about what is being saved. React escapes markup but not
 * presentation: a bidi override reverses an extension (`‮` renders
 * "gnp.exe" as "exe.png"), control characters hide text, and an unbounded
 * string pushes the location line out of view. So every plugin-supplied string
 * passes through `scrubForDisplay` before it is rendered, and a proposed name
 * is shown as it will be STORED (`previewStoredName`).
 */

/** Remove C0/C1 controls and the bidi override family, collapse whitespace,
 *  and bound the length. For display only — never what is stored. */
export function scrubForDisplay(value: string, maxLength: number): string {
  return (
    value
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f-\u009f‎‏‪-‮⁦-⁩]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maxLength)
  );
}

/**
 * A plugin-proposed name as the dialog pre-fills it: the server's own
 * transform (strip C0/DEL, `/` and `\` → `_`, trim, cap at 255) and then the
 * display scrub, so what the user sees in the name field is what their files
 * will show. Keep in step with `sanitizeName` in
 * `services/api-service/src/routes/plugins/vault-documents-bridge-handlers.ts`.
 */
export function previewStoredName(rawName: string): string {
  const asStored = rawName
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/[/\\]/g, '_')
    .trim()
    .slice(0, 255);
  return scrubForDisplay(asStored, 255) || 'Untitled';
}

/** Why a typed file name cannot be used, or `null` when it can. Mirrors the
 *  save route's schema: 1–255 characters, no slashes, no control characters. */
export function fileNameProblem(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed) return 'Give the file a name.';
  if (trimmed.length > 255) return 'That name is too long (255 characters at most).';
  if (/[/\\]/.test(trimmed)) return 'A file name can’t contain / or \\.';
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(trimmed))
    return 'That name contains characters files can’t use.';
  if (trimmed === '.' || trimmed === '..') return 'That name is reserved.';
  return null;
}

const EXTENSIONS_BY_MIME: Record<string, string> = {
  'application/pdf': '.pdf',
  'application/json': '.json',
  'application/zip': '.zip',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/msword': '.doc',
  'application/vnd.ms-excel': '.xls',
  'text/plain': '.txt',
  'text/markdown': '.md',
  'text/csv': '.csv',
  'text/html': '.html',
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/svg+xml': '.svg',
  'image/avif': '.avif',
  'audio/mpeg': '.mp3',
  'audio/wav': '.wav',
  'video/mp4': '.mp4',
};

/** The usual extension for a MIME type (".pdf"), or "" when there is none. */
export function extensionForMime(mimeType: string): string {
  return EXTENSIONS_BY_MIME[mimeType.toLowerCase().split(';')[0].trim()] ?? '';
}
